import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Extension from "@material-ui/icons/Extension";
import { useTranslation } from "react-i18next";
import { AppDispatchContext, AppStateContext } from "../../App";
import UserAPIs, { ModuleSettings } from "../../apis/user.apis";
import { isProfileFlagComplete } from "../../pages/Dashboard/DashboardModal/onboarding/onboardingWizardOrder";
import useIsMounted from "ismounted";
import ShopifyThemeEmbedPanel from "./ShopifyThemeEmbedPanel";
import { isThemeEmbedPromptDismissed } from "./themeEmbedState";
import "./ShopifyThemeEmbedRequired.scss";

const DASHBOARD_RECHECK_MS = 60 * 60 * 1000;

export default function ShopifyThemeEmbedRequired() {
    const { t } = useTranslation("shopify");
    const isMounted = useIsMounted();
    const { shop, profileSetting, loggedInUser } = useContext(AppStateContext);
    const dispatch = useContext(AppDispatchContext);
    const fetchedRef = useRef(false);
    const lastFetchAtRef = useRef(0);

    const [embedStatus, setEmbedStatus] = useState<ModuleSettings | null>(null);
    const [checking, setChecking] = useState(false);
    const [opening, setOpening] = useState(false);

    useEffect(() => {
        fetchedRef.current = false;
        lastFetchAtRef.current = 0;
    }, [shop?.customer_uid]);

    const applyEmbedStatus = useCallback((res: ModuleSettings) => {
        setEmbedStatus(res);

        if (
            dispatch
            && (res.theme_embed_setup_complete !== undefined
                || res.theme_embed_setup_acknowledged !== undefined)
        ) {
            dispatch({
                type: "set_profile_setting",
                profileSetting: {
                    ...(profileSetting ?? {}),
                    theme_embed_setup_complete: res.theme_embed_setup_complete ?? false,
                    theme_embed_setup_acknowledged: res.theme_embed_setup_acknowledged ?? false,
                },
            });
        }
    }, [dispatch, profileSetting]);

    const loadEmbedStatus = useCallback((refresh = false, acknowledge = false) => {
        if (!shop?.customer_uid) {
            return;
        }

        setChecking(true);
        new UserAPIs().modules_status(refresh, acknowledge).then((res) => {
            if (!isMounted.current) {
                return;
            }

            setChecking(false);

            if (UserAPIs.hasError(res, undefined)) {
                return;
            }

            lastFetchAtRef.current = Date.now();
            applyEmbedStatus(res);
        });
    }, [applyEmbedStatus, isMounted, shop?.customer_uid]);

    useEffect(() => {
        if (!shop?.customer_uid || fetchedRef.current) {
            return;
        }

        fetchedRef.current = true;
        loadEmbedStatus(false);
    }, [loadEmbedStatus, shop?.customer_uid]);

    useEffect(() => {
        if (!shop?.customer_uid) {
            return;
        }

        const onVisible = () => {
            if (document.visibilityState !== "visible") {
                return;
            }

            if (Date.now() - lastFetchAtRef.current < DASHBOARD_RECHECK_MS) {
                return;
            }

            loadEmbedStatus(true);
        };

        document.addEventListener("visibilitychange", onVisible);

        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [loadEmbedStatus, shop?.customer_uid]);

    const isShopifyStore = !!shop?.myshopify_domain;
    const storeIsReady =
        isProfileFlagComplete(profileSetting?.onboarding_completed)
        || isProfileFlagComplete(shop?.initialized)
        || !!loggedInUser;

    const dismissed = isThemeEmbedPromptDismissed(profileSetting, embedStatus);

    const showModal =
        isShopifyStore
        && storeIsReady
        && !dismissed
        && embedStatus?.theme_embed_required !== false
        && embedStatus?.theme_embed_enabled === false
        && !!embedStatus?.theme_embed_url;

    const openThemeEditor = () => {
        if (!embedStatus?.theme_embed_url) {
            return;
        }

        setOpening(true);
        window.open(embedStatus.theme_embed_url, "_blank", "noopener,noreferrer");
        setOpening(false);
    };

    const dismissModal = useCallback(() => {
        loadEmbedStatus(false, true);

        if (dispatch) {
            dispatch({
                type: "set_profile_setting",
                profileSetting: {
                    ...(profileSetting ?? {}),
                    theme_embed_setup_acknowledged: true,
                },
            });
        }
    }, [dispatch, loadEmbedStatus, profileSetting]);

    if (!showModal) {
        return null;
    }

    return (
        <Modal
            show
            onHide={dismissModal}
            backdrop="static"
            keyboard={false}
            centered
            dialogClassName="theme-embed-required-dialog"
        >
            <Modal.Header closeButton>
                <div className="theme-embed-required-dialog__header-main w-100">
                    <div className="theme-embed-required-dialog__icon-wrap" aria-hidden>
                        <Extension />
                    </div>
                    <div className="min-w-0">
                        <Modal.Title id="theme-embed-required-dialog-title">
                            {t("themeEmbedRequired.title")}
                        </Modal.Title>
                        <p className="theme-embed-required-dialog__subtitle">
                            {t("themeEmbedRequired.subtitleLead")}
                            {" "}
                            {t("themeEmbedRequired.subtitleBody")}
                            {" "}
                            {t("themeEmbedRequired.subtitleHint")}
                        </p>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body>
                <ShopifyThemeEmbedPanel
                    embedStatus={embedStatus}
                    checking={checking}
                    opening={opening}
                    onRefresh={() => loadEmbedStatus(true, true)}
                    onOpenThemeEditor={openThemeEditor}
                    variant="modal"
                />
            </Modal.Body>
        </Modal>
    );
}
